import { gql } from "__generated__/gql";
import { getUpdateOrCreateVariables } from "components/Shared/Forms/AddressFieldset";
import { addNewRecordsWithFieldSpec } from "utils/apollo/cacheHelpers";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { assertBrandId, assertPresence } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import {
  processApolloError,
  ManageManufacturingFacilityFormFields,
} from "./Form";
import ManufacturingFacilityFormSection from "./FormSection";

const createMutation = gql(/* GraphQL */ `
  mutation ProductLinePanelCreateManufacturingFacility(
    $facilityData: ManufacturingFacilityInput!
    $isAddressUpdatedOrCreated: Boolean = false
    $addressId: UUID
    $addressData: AddressPatch
  ) {
    ...UpdateOrCreateAddress
    createManufacturingFacility(
      input: { manufacturingFacility: $facilityData }
    ) {
      manufacturingFacility {
        id
        name
        ...ProductLinePanelManufacturingFacilityListItem
        ...ProductLinePanelEditManufacturingFacility
      }
    }
  }
`);

type Props = {
  formId: string;
  isLoading: boolean;
  onCancel: () => void;
  onCreated: () => void;
};

export default function CreateManufacturingFacilityForm({
  formId,
  isLoading,
  onCancel,
  onCreated,
}: Props) {
  const brandId = useCurrentBrandId();
  const [createFacility] = useBrandMutation(createMutation, {
    update: (cache, result) => {
      assertBrandId(brandId);

      const newFacility =
        result.data?.createManufacturingFacility?.manufacturingFacility;

      assertPresence(newFacility);

      addNewRecordsWithFieldSpec({
        cache,
        baseFieldName: "manufacturingFacilities",
        newObjs: [newFacility],
      });
    },
  });

  const [handleCreate, { isLoading: isCreating }] = useCreateValidSubmitHandler(
    async ({
      address,
      ...facilityData
    }: ManageManufacturingFacilityFormFields) => {
      assertBrandId(brandId);

      const addressVariables = getUpdateOrCreateVariables(null, address);

      assertPresence(addressVariables.addressId);

      await createFacility({
        variables: {
          facilityData: {
            ...facilityData,
            brandId,
            addressId: addressVariables.addressId,
          },
          ...addressVariables,
        },
      });
    },
    {
      onUpdateComplete: () => {
        onCreated();
      },
      processApolloError,
      successMessage: "Manufacturing facility created successfully.",
    },
  );

  return (
    <ManufacturingFacilityFormSection
      formId={formId}
      isLoading={isLoading}
      isSaving={isCreating}
      modeLabel="Create"
      onNavigateToList={onCancel}
      onCancel={onCancel}
      onValidSubmit={handleCreate}
    />
  );
}
